import React from "react";
import { Box, Heading, Text, Flex, Button } from "theme-ui";
import { Modal } from "./Modal";
import { Icon } from "./Icon";

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const features = [
    {
      icon: "vault",
      title: "Trove",
      description:
        "A Trove is your personal vault where you can deposit collateral (BNB/ETH) and borrow USDS stablecoin. Maintain a healthy collateral ratio to avoid liquidation. Think of it as your personal lending position.",
      benefits: [
        "Borrow USDS against your crypto assets",
        "0% interest rate - only pay a one-time fee",
        "Minimum collateral ratio of 110%"
      ]
    },
    {
      icon: "swimming-pool",
      title: "Stability Pool",
      description:
        "Deposit USDS into the Stability Pool to help maintain system stability by absorbing liquidations. In return, earn BNB/ETH from liquidated Troves plus SABLE token rewards.",
      benefits: [
        "Earn liquidation gains in BNB/ETH",
        "Earn SABLE token rewards",
        "Help stabilize the protocol"
      ]
    },
    {
      icon: "coins",
      title: "Staking",
      description:
        "Stake your SABLE tokens (or SABLE-BNB LP tokens) to earn protocol fees. You'll receive a share of borrowing and redemption fees generated by the protocol.",
      benefits: [
        "Earn protocol revenue share",
        "Earn BNB/ETH from redemptions",
        "Earn USDS from borrowing fees"
      ]
    }
  ];

  return (
    <Modal>
      <Box
        sx={{
          position: "relative",
          bg: "white",
          borderRadius: "16px",
          maxWidth: "800px",
          width: "90vw",
          maxHeight: "90vh",
          overflow: "auto",
          p: 4
        }}
      >
        <Flex
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            pb: 3,
            borderBottom: "2px solid #F3F4F6"
          }}
        >
          <Heading sx={{ fontSize: 4, fontWeight: "bold", color: "#000" }}>
            How Sable Finance Works
          </Heading>
          <Button
            variant="icon"
            onClick={onClose}
            sx={{
              cursor: "pointer",
              fontSize: 3,
              color: "#6B7280",
              ":hover": { color: "#000" }
            }}
          >
            <Icon name="times" size="lg" />
          </Button>
        </Flex>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                p: 3,
                bg: "#F9FAFB",
                borderRadius: "12px",
                border: "1px solid #E5E7EB"
              }}
            >
              <Heading sx={{ fontSize: 3, fontWeight: "bold", color: "#000", mb: 3 }}>
                {feature.title}
              </Heading>

              <Text sx={{ fontSize: 2, color: "#374151", mb: 3, lineHeight: 1.6 }}>
                {feature.description}
              </Text>

              <Box sx={{ pl: 3 }}>
                {feature.benefits.map((benefit, i) => (
                  <Flex key={i} sx={{ alignItems: "center", mb: 2 }}>
                    <Icon
                      name="check-circle"
                      size="sm"
                      style={{ color: "#10B981", marginRight: "8px" }}
                    />
                    <Text sx={{ fontSize: 1, color: "#6B7280" }}>{benefit}</Text>
                  </Flex>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: "2px solid #F3F4F6",
            textAlign: "center"
          }}
        >
          <Button
            onClick={() => window.open("https://sable-finance.gitbook.io/docs/", "_blank")}
            sx={{
              bg: "#F0B90B",
              color: "#000",
              px: 4,
              py: 2,
              borderRadius: "8px",
              fontWeight: "600",
              cursor: "pointer",
              ":hover": {
                bg: "#D9A000"
              }
            }}
          >
            View Documentation
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
